import Session, { SerializedSession } from './Session';
import { Time, UPFLOWY_SESSION_NAME } from '../../settings';
import ContactSenser from './ContactSenser';
import { ContactDetails, Persistence } from '../../types';

type SessionConfig = {
  workspaceId: string;
  sessionTimeout: number; // minutes
  persistence: Persistence;
  onSessionStart?: (session: Session) => Promise<void>;
  onSessionEnd?: (session: Session) => Promise<void>;
};

class SessionManager {
  static sessionKey = UPFLOWY_SESSION_NAME;

  currentSession: Session | null = null;

  config: SessionConfig;

  constructor(config: SessionConfig) {
    this.config = config;
    const workspaceSessionKey = `${config.workspaceId}${SessionManager.sessionKey}`;
    const persistedSession = config.persistence.get(workspaceSessionKey);
    if (persistedSession) {
      this.currentSession = this.constructNewSession(JSON.parse(persistedSession));
    }
  }

  getCurrentSession() {
    if (!this.currentSession) {
      this.currentSession = this.constructNewSession();
    }
    const hasExpired = this.hasExpired(this.currentSession);
    if (hasExpired) {
      this.currentSession = this.constructNewSession();
    } else {
      this.currentSession.keepActive();
    }
    this.persistCurrentSession();
    return this.currentSession;
  }

  constructNewSession(params?: SerializedSession) {
    if (this.currentSession && this.config.onSessionEnd) {
      this.config.onSessionEnd(this.currentSession);
    }
    const newSession = new Session(params);
    if (this.config.onSessionStart) {
      this.config.onSessionStart(newSession);
    }
    return newSession;
  }

  hasExpired(session: Session) {
    return Date.now() - session.lastSeenAt > this.config.sessionTimeout * Time.Minute;
  }

  persistCurrentSession() {
    const workspaceSessionKey = `${this.config.workspaceId}${SessionManager.sessionKey}`;
    if (this.currentSession) {
      this.config.persistence.set(
        workspaceSessionKey,
        JSON.stringify(this.currentSession.serialize()),
        new Date(this.currentSession.lastSeenAt + this.config.sessionTimeout * Time.Minute),
      );
    } else {
      this.config.persistence.remove(workspaceSessionKey);
    }
  }

  senseForContactDetails(event: Event, onContactsAdded: (contacts: ContactDetails[]) => void) {
    const contacts = ContactSenser.lookForContactDetails(event);
    const contactsToSync: ContactDetails[] = [];

    if (contacts?.length) {
      const existingContacts = this.getCurrentSession().contacts.filter((contact) => contact.type === 'email').map((contact) => contact.value);
      contacts.forEach((contact) => {
        if (!existingContacts.includes(contact.value)) {
          contactsToSync.push(contact);
        }
      });
    }

    if (contactsToSync.length) {
      this.getCurrentSession().addContacts(contactsToSync);
      this.persistCurrentSession();
      onContactsAdded(contactsToSync);
    }
  }
}

export default SessionManager;
